import type { Route } from 'fitify-ui-onboarding/src/types/routeTypes'

import {
  type DidYouKnowPlacement,
  type EligibilityPitchVariant,
  Funnel,
} from 'types/state'

const DID_YOU_KNOW_PATH_SEGMENT = '/did-you-know-'
const ELIGIBILITY_PITCH_PATH_SUFFIX = '/eligibility-pitch'

const PLACEMENT_ANCHOR: Record<Exclude<DidYouKnowPlacement, 'none'>, string> = {
  motivation_early: '/motivation-early',
  life_events_stopped: '/life-events-stopped',
}

/**
 * Repositions the three "Did You Know" routes based on the resolved placement
 * experiment value. The value is read from state (see PagesSideEffects)
 *
 * Defensive: a no-op (returns the routes unchanged) when the funnel has no
 * "Did You Know" routes, and drops them when the target anchor is missing.
 */
export const applyDidYouKnowPlacement = (
  routes: readonly Route<string>[],
  placement: DidYouKnowPlacement | undefined
): Route<string>[] => {
  const didYouKnowRoutes = routes.filter((route) =>
    route.path.includes(DID_YOU_KNOW_PATH_SEGMENT)
  )

  if (didYouKnowRoutes.length === 0) {
    return [...routes]
  }

  const baseRoutes = routes.filter(
    (route) => !route.path.includes(DID_YOU_KNOW_PATH_SEGMENT)
  )

  if (placement === undefined || placement === 'none') {
    return baseRoutes
  }

  const anchorIndex = baseRoutes.findIndex((route) =>
    route.path.endsWith(PLACEMENT_ANCHOR[placement])
  )

  if (anchorIndex === -1) {
    return baseRoutes
  }

  return [
    ...baseRoutes.slice(0, anchorIndex + 1),
    ...didYouKnowRoutes,
    ...baseRoutes.slice(anchorIndex + 1),
  ]
}

const getActivityAnchorPathSuffix = (funnel: Funnel) => {
  switch (funnel) {
    case Funnel.F01:
    case Funnel.GYMF01:
    case Funnel.HOPIF01:
    case Funnel.MILICAF01:
    case Funnel.WALLPIF01:
      return '/fitness-level-plank'
    case Funnel.M01:
    case Funnel.MILICAM01:
      return '/flexible'
    case Funnel.CHF01:
    case Funnel.CHM01:
    case Funnel.TCHF01:
    case Funnel.TCHM01:
      return '/how-often-walks'
    default:
      return undefined
  }
}

const getEligibilityPitchAnchorPathSuffix = (
  variant: EligibilityPitchVariant,
  funnel: Funnel
) => {
  switch (variant) {
    case 'eligible_activity':
    case 'confident_activity':
      return getActivityAnchorPathSuffix(funnel)
    case 'eligible_almost_there':
    case 'confident_almost_there':
      return '/life-events-stopped'
    case 'none':
    default:
      return undefined
  }
}

/**
 * Positions the eligibility pitch route after the funnel's activity anchor step
 * or after life-events-stopped, based on the resolved variant (see
 * PagesSideEffects).
 * Must run after applyDidYouKnowPlacement so the pitch precedes the
 * did-you-know steps when both anchor on life-events-stopped.
 */
export const applyEligibilityPitchPlacement = (
  routes: readonly Route<string>[],
  variant: EligibilityPitchVariant | undefined,
  funnel: Funnel | undefined
): Route<string>[] => {
  const eligibilityPitchRoutes = routes.filter((route) =>
    route.path.endsWith(ELIGIBILITY_PITCH_PATH_SUFFIX)
  )

  if (eligibilityPitchRoutes.length === 0) {
    return [...routes]
  }

  const baseRoutes = routes.filter(
    (route) => !route.path.endsWith(ELIGIBILITY_PITCH_PATH_SUFFIX)
  )

  const anchorPathSuffix =
    variant !== undefined && funnel !== undefined
      ? getEligibilityPitchAnchorPathSuffix(variant, funnel)
      : undefined

  if (anchorPathSuffix === undefined) {
    return baseRoutes
  }

  const anchorIndex = baseRoutes.findIndex((route) =>
    route.path.endsWith(anchorPathSuffix)
  )

  if (anchorIndex === -1) {
    return baseRoutes
  }

  return [
    ...baseRoutes.slice(0, anchorIndex + 1),
    ...eligibilityPitchRoutes,
    ...baseRoutes.slice(anchorIndex + 1),
  ]
}
